import i18n, { type TFunction } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, NAMESPACES } from './config';
import {
  detectLanguage,
  persistLanguage,
  readQueryLocale
} from './detect-language';
import { resources } from './resources';

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: detectLanguage(),
    fallbackLng: DEFAULT_LOCALE,
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    },
    returnNull: false,
    saveMissing: process.env.NODE_ENV !== 'production',
    missingKeyHandler: (lngs, ns, key) => {
      if (
        process.env.NODE_ENV !== 'production' &&
        typeof console !== 'undefined'
      ) {
        // biome-ignore lint/suspicious/noConsole: dev-only missing-key diagnostic
        console.warn(
          `[i18n] missing key "${ns}:${key}" for locale(s) ${lngs.join(', ')}`
        );
      }
    }
  });

  // A language forced via `?lang=` overrides and is persisted so it sticks
  // across SPA navigation and later visits (QR-code marketing entry points).
  const forcedLocale = readQueryLocale();
  if (forcedLocale) {
    persistLanguage(forcedLocale);
  }
}

// Escape hatch for dynamic translation keys (keys derived from variables instead
// of literals). i18next v25's typed `t()` requires literal-typed keys, and JSON
// imports widen to plain string, so dynamic keys can't satisfy the constraint.
// Callers using static literal keys should keep calling `t()` directly so they
// get key autocomplete and typo detection.
export const tDynamic = (
  t: TFunction,
  key: string,
  vars?: Record<string, unknown>
): string => (vars ? t(key as never, vars) : t(key as never));

export default i18n;
